/**
 * Shape of a file a tool run produced, and the checks that read it off an untyped tool result.
 * Keyed on field names, not tool names, so any tool reporting this shape gets the artifact strip.
 */

import { isPlainObject } from "./isPlainObject";

/** The statuses this frontend has copy for. Only `uploaded` files are downloadable. */
export type ArtifactOutputStatus =
  | "uploaded"
  | "too_large"
  | "too_many"
  | "empty"
  | "budget_exhausted"
  | "upload_failed"
  | "unavailable";

export interface ArtifactOutput {
  name: string;
  bytes: number;
  content_type: string;
  /** Left open: the server's status is a free-form `str`, so newer statuses must still parse. */
  status: ArtifactOutputStatus | (string & {});
}

const NOT_RETURNED_REASONS: Partial<Record<string, string>> = {
  too_large: "Too large to return",
  too_many: "Skipped — too many files were produced in this run",
  empty: "The file was empty",
  budget_exhausted: "Skipped — this run's output budget was used up",
  upload_failed: "Upload failed",
  unavailable: "No longer available",
};

/** Why a produced file was not returned, with a generic fallback for unknown statuses. */
export function notReturnedReason(status: string): string {
  return NOT_RETURNED_REASONS[status] ?? "Not returned";
}

const OUTPUTS_FIELD = "outputs";

export interface OutputsField {
  artifacts: ArtifactOutput[];
  /** The key this came from, so the caller can omit it from the JSON view. */
  fieldName: string;
}

/**
 * Read the artifacts off a payload's `outputs` array, or null when there are none (an empty array
 * counts as none, so the strip never renders empty).
 */
export function extractOutputsField(output: unknown): OutputsField | null {
  if (!isPlainObject(output)) {
    return null;
  }
  const value = output[OUTPUTS_FIELD];
  if (!Array.isArray(value) || value.length === 0) {
    return null;
  }
  const artifacts = value.filter(_isArtifactOutput);
  if (artifacts.length === 0) {
    return null;
  }
  return { artifacts, fieldName: OUTPUTS_FIELD };
}

/**
 * Read one tool result's artifacts off its `artifacts` field, or null when it reports none. Don't
 * fall back to `raw_response`: thread GETs always null it. Malformed entries are dropped singly.
 */
export function resolveArtifacts(toolResult: {
  artifacts?: unknown;
}): ArtifactOutput[] | null {
  if (!Array.isArray(toolResult.artifacts)) {
    return null;
  }
  const artifacts = toolResult.artifacts.filter(_isArtifactOutput);
  return artifacts.length > 0 ? artifacts : null;
}

function _isArtifactOutput(value: unknown): value is ArtifactOutput {
  return (
    isPlainObject(value) &&
    typeof value.name === "string" &&
    value.name.length > 0 &&
    typeof value.bytes === "number" &&
    typeof value.content_type === "string" &&
    value.content_type.length > 0 &&
    typeof value.status === "string" &&
    value.status.length > 0
  );
}
